import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import {
  FiDatabase, FiUploadCloud, FiSearch, FiTrash2, FiDownload, FiFile, FiFolder, FiLayers, FiRefreshCw, FiCheckCircle
} from 'react-icons/fi';

/* ══ CONQUER Cyan Theme ══ */
const THEME = {
  primary: '#00B4D8',
  secondary: '#48CAE4',
  accent: '#90E0EF',
  success: '#059669',
  warning: '#D97706',
  danger: '#DC2626',
  dark: '#0A1628',
  slate: '#0096C7',
  muted: '#48CAE4',
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div style={{
    background: '#fff', borderRadius: 14, padding: 20,
    border: `1px solid ${THEME.accent}30`, boxShadow: '0 2px 12px rgba(124,58,237,0.06)',
    display: 'flex', alignItems: 'center', gap: 14,
  }}>
    <div style={{ width: 48, height: 48, borderRadius: 12, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={24} style={{ color }} />
    </div>
    <div>
      <div style={{ fontSize: 24, fontWeight: 800, color: THEME.dark }}>{value}</div>
      <div style={{ fontSize: 12, color: THEME.muted, fontWeight: 600 }}>{label}</div>
    </div>
  </div>
);

export default function MasterDataPage() {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({ total: 0, categories: 0, sources: 0, readyToUse: 0 });
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileRef = useRef();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/master-data?page=${page}&search=${search}&category=${category}`);
      setRecords(data.records || []);
      setPages(data.pages || 1);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  }, [page, search, category]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/master-data/stats');
      setStats({
        total: data.total || 0,
        categories: data.categoriesCount || 0,
        sources: data.sourcesCount || 0,
        readyToUse: data.readyToUse || 0,
      });
      if (data.total > 0) setPages(Math.ceil(data.total / 50));
    } catch {}
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/admin/master-data/categories');
      setCategories(data || []);
    } catch {}
  };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchCategories(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    setUploadProgress(0);

    try {
      await api.post('/admin/master-data/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total)),
      });
      toast.success('Data uploaded successfully');
      fetchData();
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.message || 'Upload failed'); }
    finally { setUploading(false); setUploadProgress(0); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try { await api.delete(`/admin/master-data/${id}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/admin/master-data/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'master_data_export.csv'; a.click();
      toast.success('Export started');
    } catch { toast.error('Export failed'); }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '2px solid #E0E7FF',
    fontSize: 14, color: THEME.dark, background: '#FAFAFF', outline: 'none',
  };

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: THEME.dark, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiDatabase size={28} style={{ color: THEME.primary }} />
            Arsenal Hub
          </h1>
          <p style={{ fontSize: 14, color: THEME.muted, marginTop: 6 }}>Centralized data repository</p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={handleExport}
            style={{ padding: '12px 20px', borderRadius: 12, border: '1px solid #E0E7FF', background: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: THEME.slate }}>
            <FiDownload size={18} /> Export
          </button>
          <label style={{
            padding: '12px 24px', borderRadius: 12,
            background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`,
            color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: `0 6px 20px ${THEME.primary}30`,
          }}>
            <FiUploadCloud size={18} /> {uploading ? `Uploading ${uploadProgress}%` : 'Upload CSV'}
            <input ref={fileRef} type="file" accept=".csv" onChange={handleUpload} hidden disabled={uploading} />
          </label>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard icon={FiLayers} label="Total Records" value={stats.total.toLocaleString()} color={THEME.primary} />
        <StatCard icon={FiFolder} label="Categories" value={stats.categories} color={THEME.success} />
        <StatCard icon={FiFile} label="Data Sources" value={stats.sources} color={THEME.slate} />
        <StatCard icon={FiCheckCircle} label="Ready to Use" value={stats.readyToUse.toLocaleString()} color={THEME.warning} />
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div style={{
          background: '#fff', borderRadius: 14, padding: 20, marginBottom: 24,
          border: `1px solid ${THEME.accent}30`, boxShadow: '0 2px 12px rgba(124,58,237,0.06)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: THEME.dark }}>Uploading...</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: THEME.primary }}>{uploadProgress}%</span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: '#E0E7FF', overflow: 'hidden' }}>
            <div style={{ width: `${uploadProgress}%`, height: '100%', background: `linear-gradient(90deg, ${THEME.primary}, ${THEME.secondary})`, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{
        background: '#fff', borderRadius: 14, padding: 16, marginBottom: 24,
        border: `1px solid ${THEME.accent}30`, boxShadow: '0 2px 12px rgba(124,58,237,0.06)',
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 200 }}>
          <FiSearch size={20} style={{ color: THEME.muted }} />
          <input
            type="text" placeholder="Search records..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ ...inputStyle, border: 'none', background: 'transparent', padding: 0 }}
          />
        </div>
        <select
          value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}
          style={{ ...inputStyle, width: 'auto', minWidth: 160, cursor: 'pointer' }}
        >
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={fetchData}
          style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid #E0E7FF', background: '#fff', cursor: 'pointer', color: THEME.slate, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiRefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div style={{
        background: '#fff', borderRadius: 18, overflow: 'hidden',
        border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)',
      }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ width: 48, height: 48, border: '4px solid #E0E7FF', borderTopColor: THEME.primary, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${THEME.primary}08, ${THEME.accent}05)` }}>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase', letterSpacing: 0.5 }}>Name / Email</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Phone</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Category</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Source</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Added</th>
                  <th style={{ padding: '16px 20px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => (
                  <tr key={r._id} style={{ borderBottom: '1px solid #F0EEFF', background: i % 2 === 0 ? '#fff' : '#FAFAFF' }}>
                    <td style={{ padding: '14px 20px' }}>
                      <div>
                        <div style={{ fontWeight: 700, color: THEME.dark, fontSize: 14 }}>{r.name || 'N/A'}</div>
                        <div style={{ fontSize: 12, color: THEME.muted }}>{r.email || '-'}</div>
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px', color: THEME.slate, fontSize: 13, fontWeight: 500 }}>{r.phone || r.mobile || '-'}</td>
                    <td style={{ padding: '14px 20px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: `${THEME.primary}10`, color: THEME.primary }}>
                        {r.category || 'Uncategorized'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 20px', color: THEME.muted, fontSize: 13 }}>{r.source || 'Manual'}</td>
                    <td style={{ padding: '14px 20px', color: THEME.muted, fontSize: 13 }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '-'}</td>
                    <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                      <button onClick={() => handleDelete(r._id)} title="Delete"
                        style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: `${THEME.danger}15`, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: THEME.danger, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <FiTrash2 size={14} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {records.length === 0 && (
                  <tr><td colSpan="6" style={{ padding: 60, textAlign: 'center', color: THEME.muted }}>
                    <FiDatabase size={48} style={{ marginBottom: 16, opacity: 0.4 }} />
                    <div style={{ fontSize: 16, fontWeight: 600 }}>No data found</div>
                    <div style={{ fontSize: 13, marginTop: 6 }}>Upload a CSV to get started</div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: 20, borderTop: '1px solid #F0EEFF' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #E0E7FF', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: THEME.slate }}>‹</button>
            {[...Array(Math.min(pages, 7))].map((_, i) => (
              <button key={i} onClick={() => setPage(i + 1)}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: page === i + 1 ? THEME.primary : '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: page === i + 1 ? '#fff' : THEME.slate }}>{i + 1}</button>
            ))}
            {pages > 7 && <span style={{ padding: '8px 10px', color: THEME.muted }}>...</span>}
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #E0E7FF', background: '#fff', cursor: page === pages ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: THEME.slate }}>›</button>
          </div>
        )}
      </div>
    </div>
  );
}
